import { useState } from 'react';
import { parseBulkLine } from '../lib/courseParser.js';
import { parseFreeformText } from '../lib/llmParser.js';
import { DIRECTION_BEARINGS } from '../lib/courseConstants.js';

/**
 * Flexible paste import for course data.
 * Supports tabular/CSV/prose formats with LLM fallback for ambiguous input.
 */
export default function PasteImport({ card, marks, onImport, onClose }) {
  const [rawText, setRawText] = useState('');
  const [parsedResults, setParsedResults] = useState(null);
  const [aiParsing, setAiParsing] = useState(false);
  const [aiError, setAiError] = useState('');

  function handlePreview() {
    if (!rawText.trim()) return;

    const lines = rawText.trim().split('\n');
    const results = lines.map((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) {
        return { line: trimmed, status: 'skip', course: null, message: 'Comment or empty' };
      }

      const course = parseBulkLine(trimmed);
      if (course) {
        return { line: trimmed, status: 'ok', course, message: null };
      }

      return { line: trimmed, status: 'error', course: null, message: 'Could not parse' };
    });

    setParsedResults(results);
    setAiError('');
  }

  async function handleAIParse() {
    if (!parsedResults) return;

    const failedLines = parsedResults
      .filter((r) => r.status === 'error')
      .map((r) => r.line)
      .join('\n');

    if (!failedLines) return;

    setAiParsing(true);
    setAiError('');

    try {
      const { courses, errors } = await parseFreeformText(failedLines);

      // Merge AI results back into parsedResults
      const aiCourseMap = new Map();
      for (const c of courses) {
        const key = `${c.directionCode}${c.courseNumber}`;
        aiCourseMap.set(key, {
          id: crypto.randomUUID(),
          directionCode: c.directionCode,
          courseNumber: c.courseNumber,
          markIds: c.markIds,
          markDirections: c.markIds.map(() => 'port'), // default, will be inferred
          bearing: Math.round(DIRECTION_BEARINGS[c.directionCode] || 0),
          distance: 0,
        });
      }

      const aiErrorSet = new Set(errors.map((e) => e.line || e.raw));

      setParsedResults((prev) =>
        prev.map((r) => {
          if (r.status !== 'error') return r;

          // Check if AI parsed this line by matching against results
          for (const [, course] of aiCourseMap) {
            // Simple heuristic: if the line contains the direction code + number
            const key = `${course.directionCode}${course.courseNumber}`;
            if (r.line.includes(course.directionCode) || r.line.includes(key)) {
              aiCourseMap.delete(key);
              return { ...r, status: 'ai', course, message: 'Parsed by AI — review' };
            }
          }

          if (aiErrorSet.has(r.line)) {
            const err = errors.find((e) => e.line === r.line);
            return { ...r, status: 'error', message: err?.error || 'AI could not parse' };
          }

          return r;
        }),
      );

      // Any remaining AI courses not matched to lines — add them
      for (const [, course] of aiCourseMap) {
        setParsedResults((prev) => [
          ...prev,
          { line: '(AI)', status: 'ai', course, message: 'Parsed by AI — review' },
        ]);
      }
    } catch (err) {
      setAiError(`AI parsing failed: ${err.message}`);
    } finally {
      setAiParsing(false);
    }
  }

  function handleImport() {
    if (!parsedResults) return;

    const courses = parsedResults
      .filter((r) => (r.status === 'ok' || r.status === 'ai') && r.course)
      .map((r) => r.course);

    if (courses.length > 0) {
      onImport(courses);
      setRawText('');
      setParsedResults(null);
    }
  }

  const okCount = parsedResults?.filter((r) => r.status === 'ok' || r.status === 'ai').length ?? 0;
  const errorCount = parsedResults?.filter((r) => r.status === 'error').length ?? 0;

  return (
    <div className="paste-import">
      <div className="paste-import-header">
        <h3>Import from text{card?.name ? ` — ${card.name}` : ''}</h3>
        {onClose && (
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        )}
      </div>

      <label className="form-field">
        <span className="form-label">
          Paste course data
          <span className="form-hint">
            Supported: A1 ADEFS, tab-separated, CSV, or freeform text
          </span>
        </span>
        <textarea
          value={rawText}
          onChange={(e) => { setRawText(e.target.value); setParsedResults(null); }}
          placeholder={`# Examples:\nA1 ApBsCpDs\nB3 EFGHA\nC1,ApDsEp\n\n# Or paste freeform text and use AI parse`}
          rows={8}
          spellCheck={false}
          className="mono-input"
        />
      </label>

      <div className="paste-import-actions">
        <button
          className="btn btn-primary"
          onClick={handlePreview}
          disabled={!rawText.trim()}
        >
          Preview
        </button>
        {parsedResults && errorCount > 0 && (
          <button
            className="btn"
            onClick={handleAIParse}
            disabled={aiParsing}
          >
            {aiParsing ? 'Parsing with AI...' : `Parse ${errorCount} failed line${errorCount > 1 ? 's' : ''} with AI`}
          </button>
        )}
      </div>

      {aiError && <p className="error-text">{aiError}</p>}

      {/* Parse results table */}
      {parsedResults && (
        <div className="paste-import-results">
          <p className="paste-import-summary">
            {okCount} parsed, {errorCount} failed,{' '}
            {parsedResults.filter((r) => r.status === 'skip').length} skipped
          </p>

          <table className="paste-results-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Input</th>
                <th>Course</th>
                <th>Marks</th>
              </tr>
            </thead>
            <tbody>
              {parsedResults
                .filter((r) => r.status !== 'skip')
                .map((r, i) => (
                  <tr key={i} className={`paste-result-${r.status}`}>
                    <td>
                      <span className={`status-dot ${r.status}`}>
                        {r.status === 'ok' ? 'OK' : r.status === 'ai' ? 'AI' : 'ERR'}
                      </span>
                    </td>
                    <td className="mono-text">{r.line}</td>
                    <td>
                      {r.course && (
                        <code>{r.course.directionCode}{r.course.courseNumber}</code>
                      )}
                    </td>
                    <td>
                      {r.course ? (
                        <code>{r.course.markIds.join('')}</code>
                      ) : (
                        <span className="error-text">{r.message}</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={okCount === 0}
          >
            Import {okCount} course{okCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
}
